import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uid } from 'react-uid';
import { UserAvatar } from '../../../../../components/Avatar/User';
import { ReloadContainerNavItem } from '../../../../../components/ReloadContainerNavItem';
import {
  ContainerHeader,
  ContainerHeaderLoading,
  HeaderSection,
  Item,
  ItemLoading,
  MenuSection,
  NavigationView,
  Separator,
} from '../../../../../design-system';
import { getCurrentOrganisationUserAction } from '../../../../../store/organisationUser/actions';
import { fullStateSelector } from '../../../../../store/root';

const Items: React.FC = (): JSX.Element => {
  const {
    organisation: { current: organisation, loading: orgLoading },
    organisationUser: { error, loading, current: organisationUser },
  } = useSelector(fullStateSelector);
  const organisationId = organisation?.id;
  const dispatch = useDispatch();

  const refetch = React.useCallback(() => {
    if (organisationId && organisationUser) {
      dispatch(getCurrentOrganisationUserAction({ organisationId, userId: organisationUser.id }));
    }
  }, [dispatch, organisationUser, organisationId]);

  const ReloadButton = () => <ReloadContainerNavItem loading={loading} descriptor="user" onClick={refetch} />;

  if (loading || orgLoading) {
    return (
      <>
        <div data-testid="skeleton">
          <HeaderSection>{() => <ContainerHeaderLoading avatar subText />}</HeaderSection>
          <Separator mb={1} />
          <MenuSection canOverflow>
            {() =>
              Array(3)
                .fill(undefined)
                .map((_, idx) => <ItemLoading key={uid(idx)} />)
            }
          </MenuSection>
        </div>
        <ReloadButton />
      </>
    );
  }

  if (!organisationUser || !organisation) {
    return (
      <>
        <HeaderSection>
          {() => (
            <ContainerHeader
              avatar={<UserAvatar embedded user={organisationUser ?? {}} />}
              text={organisationUser?.name ?? 'Unknown'}
              subText="User"
            />
          )}
        </HeaderSection>
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderSection>
          {() => (
            <ContainerHeader
              avatar={<UserAvatar embedded user={organisationUser} />}
              text={organisationUser?.name ?? 'Unknown'}
              subText="User"
            />
          )}
        </HeaderSection>
        <Separator mb={1} />
        <ReloadButton />
      </>
    );
  }

  const { id, name } = organisationUser;

  return (
    <>
      <HeaderSection>
        {() => <ContainerHeader avatar={<UserAvatar embedded user={organisationUser} />} text={name} subText="User" />}
      </HeaderSection>
      <Separator mb={1} />
      <MenuSection canOverflow>
        {() => (
          <>
            <Item text="Overview" to={`/organisations/${organisation.slug}/users/${id}/overview`} />
            <Item text="Locations" to={`/organisations/${organisation.slug}/users/${id}/locations`} />
            <Item text="Settings" to={`/organisations/${organisation.slug}/users/${id}/settings`} />
          </>
        )}
      </MenuSection>
      <ReloadButton />
    </>
  );
};

export const organisationUserNav: NavigationView = {
  id: 'organisationUser/container',
  type: 'container',
  Items,
};
